import React, { useState, useEffect } from 'react'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TextField from '@mui/material/TextField'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import PeopleAltIcon from '@mui/icons-material/PeopleAlt'
import LockIcon from '@mui/icons-material/Lock'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Box, Tooltip } from '@mui/material'

import { useAppSelector } from '../hooks'
import phaserGame from '../PhaserGame'
import Bootstrap from '../scenes/Bootstrap'
import { PermissionsDialog } from './PermissionsDialog'
import { phaserEvents } from '../events/EventCenter'

// Import styled components
import {
  MessageText,
  CustomRoomTableContainer,
  TableRowWrapper,
  ActionButton,
  PasswordDialog,
  LoadingState,
} from './styles/CustomRoomTable.styles'

// Define room interface
interface RoomData {
  roomId: string
  name: string
  description?: string
  creatorName?: string
  password?: string | null
  clients?: number
  metadata?: {
    playerName?: string
    creatorId?: string
    [key: string]: any
  }
}

interface CustomRoomTableProps {
  /** Callback function when user wants to go back */
  onBack: () => void
}

interface PendingRoomJoin {
  roomId: string
  password: string | null
}

const checkRoomHasPassword = (room: RoomData): boolean => {
  // Check all possible password indicators
  return !!(
    (
      room.password || // Direct password property
      room.metadata?.hasPassword || // Metadata hasPassword flag
      room.metadata?.isPrivate || // Metadata isPrivate flag
      room.metadata?.passwordProtected || // Metadata passwordProtected flag
      (room.metadata && room.metadata.hasPassword === true)
    ) // Explicit boolean check
  )
}

export const CustomRoomTable: React.FC<CustomRoomTableProps> = ({ onBack }) => {
  const [password, setPassword] = useState('')
  const [selectedRoom, setSelectedRoom] = useState('')
  const [showPasswordDialog, setShowPasswordDialog] = useState(false)
  const [showPasswordError, setShowPasswordError] = useState(false)
  const [passwordFieldEmpty, setPasswordFieldEmpty] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false)
  const [pendingRoomJoin, setPendingRoomJoin] = useState<PendingRoomJoin | null>(null)
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true)

  const lobbyJoined = useAppSelector((state) => state.room.lobbyJoined)
  const availableRooms = useAppSelector((state) => state.room.availableRooms)

  useEffect(() => {
    const refreshInterval = setInterval(() => {
      if (lobbyJoined && autoRefreshEnabled) {
        refreshRoomData(false)
      }
    }, 30000)

    return () => {
      clearInterval(refreshInterval)
    }
  }, [lobbyJoined, autoRefreshEnabled])

  useEffect(() => {
    const handlePlayerCountUpdate = (data: { id: string; playerCount: number }) => {
      console.log(
        `CustomRoomTable received count update for room ${data.id}: ${data.playerCount} players`
      )
    }

    phaserEvents.on('roomPlayerCount', handlePlayerCountUpdate)
    return () => {
      phaserEvents.off('roomPlayerCount', handlePlayerCountUpdate)
    }
  }, [])

  useEffect(() => {
    // Log available rooms whenever they change
    if (availableRooms.length > 0) {
      console.log(
        'Available rooms updated:',
        availableRooms.map((room) => ({
          id: room.roomId,
          name: room.name,
          clients: room.clients,
          hasPassword: !!room.password,
          metadata: room.metadata,
        }))
      )
    }
  }, [availableRooms])

  const refreshRoomData = async (showLoading = true) => {
    if (showLoading) {
      setIsLoading(true)
    }

    try {
      console.log('Refreshing room data...')
      const bootstrap = phaserGame.scene.keys.bootstrap as Bootstrap
      await bootstrap.network.joinLobbyRoom()

      // Wait a moment for room list to update
      setTimeout(() => {
        console.log('Room data refreshed')
        if (showLoading) {
          setIsLoading(false)
        }
      }, 500)
    } catch (error) {
      console.error('Failed to refresh rooms:', error)
      setIsLoading(false)
    }
  }

  const handleJoinClick = async (roomId: string, password: string | null) => {
    console.log(`Preparing to join room ${roomId} with${password ? '' : 'out'} password`)

    // Store the roomId in localStorage for persistence
    localStorage.setItem('pendingRoomId', roomId)

    // Get the room details to check if it requires a password
    const roomToJoin = availableRooms.find((r) => r.roomId === roomId)

    if (roomToJoin) {
      const hasPassword = checkRoomHasPassword(roomToJoin)

      console.log('Room details:', {
        id: roomToJoin.roomId,
        name: roomToJoin.name,
        hasPassword,
        metadata: roomToJoin.metadata,
      })

      // Set flag in localStorage about password requirement
      if (hasPassword) {
        localStorage.setItem('roomHasPassword', 'true')
      } else {
        localStorage.removeItem('roomHasPassword')
      }

      // Check if room requires password
      if (hasPassword && !password) {
        // Show password dialog instead of permissions dialog
        setSelectedRoom(roomId)
        setShowPasswordDialog(true)
        return
      }

      // If room has password and it was provided, validate it first
      if (hasPassword && password) {
        try {
          setIsLoading(true)

          // Get Bootstrap scene to access the network
          const bootstrap = phaserGame.scene.keys.bootstrap as Bootstrap
          if (!bootstrap || !bootstrap.network) {
            throw new Error('Network not initialized')
          }

          // Validate password with server
          const validation = await bootstrap.network.validateRoomPassword(roomId, password)
          setIsLoading(false)

          // Check validation result
          if (
            validation.valid === true ||
            (validation.valid === 'unknown' && validation.requiresJoin)
          ) {
            console.log('Password accepted or requires direct join attempt')
            // Password is valid or requires direct join, proceed to permissions dialog
            localStorage.setItem('pendingRoomPassword', password)
            setPendingRoomJoin({ roomId, password })
            setShowPermissionsDialog(true)
          } else {
            // Invalid password
            console.error('Invalid password:', validation.message)
            setShowPasswordError(true)
            setShowPasswordDialog(true)
            setSelectedRoom(roomId)
          }

          return
        } catch (error) {
          console.error('Error validating password:', error)
          setIsLoading(false)
          // Fall through to the normal flow as a fallback
        }
      }
    }

    // If we get here, either room doesn't need password or validation is bypassed
    if (password) {
      localStorage.setItem('pendingRoomPassword', password)
    } else {
      localStorage.removeItem('pendingRoomPassword')
    }

    setPendingRoomJoin({ roomId, password })
    setShowPermissionsDialog(true)
  }

  const joinColyseusRoom = async (roomId: string, password: string | null) => {
    try {
      if (!lobbyJoined) {
        console.error('Lobby not joined, cannot connect to room')
        // Attempt to reconnect lobby in background
        try {
          const bootstrap = phaserGame.scene.keys.bootstrap as Bootstrap
          bootstrap.network.joinLobbyRoom().catch(() => {})
        } catch { /* ignore */ }
        return
      }

      // Double check the room ID from localStorage as a fallback
      const storedRoomId = localStorage.getItem('pendingRoomId')
      if (!roomId && storedRoomId) {
        console.log(`No roomId provided, using stored ID: ${storedRoomId}`)
        roomId = storedRoomId
      }

      if (!roomId) {
        console.error('No valid room ID available')
        setIsLoading(false)
        return
      }

      // Double check the password from localStorage if needed
      if (!password && localStorage.getItem('pendingRoomPassword')) {
        password = localStorage.getItem('pendingRoomPassword')
        console.log('Using stored password from localStorage')
      }

      setIsLoading(true)
      const bootstrap = phaserGame.scene.keys.bootstrap as Bootstrap

      console.log(`Attempting to join room with ID: ${roomId}`)

      try {
        const success = await bootstrap.network.joinCustomById(roomId, password)

        if (success) {
          console.log('Successfully joined room, launching game...')
          // Clear stored room data on successful join
          localStorage.removeItem('pendingRoomId')
          localStorage.removeItem('pendingRoomPassword')

          // Get the room state to extract mapId if available
          const roomState = bootstrap.network.getRoomState()
          const mapId = roomState?.mapId
          console.log(`Launching game with map ID from room state: ${mapId}`)

          // Launch game with the correct map ID
          bootstrap.launchGame({
            mapId: mapId,
            fromRoomCreation: false,
          })
        } else {
          console.error('Failed to join room: joinCustomById returned false')
          setIsLoading(false)

          // Room join failed - show generic error
          setSelectedRoom(roomId)
          setShowPasswordError(true)

          // If room has password, show password dialog to retry
          const roomHasPassword = localStorage.getItem('roomHasPassword') === 'true'
          if (roomHasPassword) {
            setShowPasswordDialog(true)
          }
        }
      } catch (error: any) {
        console.error('Error joining room:', error)
        setIsLoading(false)

        const joinErrMsg = typeof error?.message === 'string' ? error.message : ''
        const joinErrCode = error?.code
        // Password / auth failures (including 503 / hash unavailable from server)
        const isPasswordRelatedJoinError =
          (joinErrMsg &&
            (joinErrMsg.includes('password') ||
              joinErrMsg.includes('403') ||
              joinErrMsg.includes('503') ||
              joinErrMsg.includes('Invalid password') ||
              joinErrMsg.toLowerCase().includes('not available'))) ||
          joinErrCode === 403 ||
          joinErrCode === 503

        if (isPasswordRelatedJoinError) {
          console.log('Password validation failed, showing password dialog')
          setSelectedRoom(roomId)
          setShowPasswordError(true)
          setShowPasswordDialog(true)
        } else {
          // For other errors, retry once
          console.log('Retrying room join after delay...')
          setTimeout(() => {
            // Only retry if we aren't showing the password dialog
            if (!showPasswordDialog) {
              joinColyseusRoom(roomId, password)
            }
          }, 2000)
        }
      }
    } catch (error) {
      console.error('Outer error joining room:', error)
      setIsLoading(false)

      // Try to recover if possible
      const storedRoomId = localStorage.getItem('pendingRoomId')
      const storedPassword = localStorage.getItem('pendingRoomPassword')
      if (storedRoomId) {
        console.log(`Retrieving stored room info: ${storedRoomId}`)
        joinColyseusRoom(storedRoomId, storedPassword)
      }
    }
  }

  const handlePermissionsGranted = () => {
    if (pendingRoomJoin) {
      console.log(`Permissions granted, joining room ${pendingRoomJoin.roomId}`)

      // Additional check to ensure Bootstrap is ready
      if (!phaserGame.scene.keys.bootstrap) {
        console.error('Bootstrap scene not available')
        setIsLoading(false)
        return
      }

      const bootstrap = phaserGame.scene.keys.bootstrap as Bootstrap
      if (!bootstrap.network) {
        console.error('Network not initialized in Bootstrap')
        setIsLoading(false)
        return
      }

      joinColyseusRoom(pendingRoomJoin.roomId, pendingRoomJoin.password)
      setPendingRoomJoin(null)
    } else {
      console.warn('Permission granted but no pending room join request')
      // Try to retrieve room info from localStorage as fallback
      const storedRoomId = localStorage.getItem('pendingRoomId')
      if (storedRoomId) {
        const storedPassword = localStorage.getItem('pendingRoomPassword')
        console.log(`Retrieving stored room info: ${storedRoomId}`)
        joinColyseusRoom(storedRoomId, storedPassword)
      }
    }
  }

  const handlePasswordSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsLoading(true)
    setShowPasswordError(false)

    try {
      if (!lobbyJoined) {
        throw new Error('Not connected to server')
      }

      // Get Bootstrap scene to access the network
      const bootstrap = phaserGame.scene.keys.bootstrap as Bootstrap
      if (!bootstrap || !bootstrap.network) {
        throw new Error('Network not initialized')
      }

      // Validate password with server
      const validation = await bootstrap.network.validateRoomPassword(selectedRoom, password)

      // Check validation result
      if (validation.valid === true) {
        console.log('Password validated successfully')
        setIsLoading(false)
        resetPasswordDialog()

        // Proceed to permissions dialog with validated password
        setPendingRoomJoin({ roomId: selectedRoom, password })
        setShowPermissionsDialog(true)
      } else if (validation.valid === 'unknown' && validation.requiresJoin) {
        // Some custom rooms require direct join attempt to validate password
        console.log('Custom room requires direct join to validate password')
        setIsLoading(false)
        resetPasswordDialog()

        // Proceed to permissions dialog with the password, which will be validated during join
        setPendingRoomJoin({ roomId: selectedRoom, password })
        setShowPermissionsDialog(true)
      } else {
        // Invalid password
        console.error('Invalid password:', validation.message)
        setShowPasswordError(true)
        setIsLoading(false)
      }
    } catch (error) {
      console.error('Error validating password:', error)
      setShowPasswordError(true)
      setIsLoading(false)
    }
  }

  const resetPasswordDialog = () => {
    setShowPasswordDialog(false)
    setPassword('')
    setPasswordFieldEmpty(false)
    setShowPasswordError(false)
  }

  // Only blank the table when there is nothing in it yet: gating on isLoading alone
  // replaced an already-populated list with a spinner on every refresh. [SC-313]
  if (isLoading && availableRooms.length === 0) {
    return (
      <LoadingState>
        <CircularProgress sx={{ color: '#42eacb' }} />
      </LoadingState>
    )
  }

  return availableRooms.length === 0 ? (
    <MessageText>
      There are no custom rooms available. Create one or join the public lobby.
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={async () => {
            setIsLoading(true)
            try {
              const bootstrap = phaserGame.scene.keys.bootstrap as Bootstrap
              await bootstrap.network.joinLobbyRoom()
              setTimeout(() => setIsLoading(false), 500)
            } catch (error) {
              console.error('Failed to refresh rooms:', error)
              setIsLoading(false)
            }
          }}
        >
          Refresh Rooms
        </Button>
      </Box>
    </MessageText>
  ) : (
    <>
      <CustomRoomTableContainer component={Paper}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 16px' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={
              isLoading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />
            }
            disabled={isLoading}
            onClick={() => refreshRoomData(true)}
          >
            {isLoading ? 'Refreshing…' : 'Refresh'}
          </Button>
        </Box>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Room Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Created By</TableCell>
              <TableCell>Players</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* The outer early-return above already handles "loading with nothing to show".
                Reaching here with isLoading true means a *refresh* over a populated list, and
                branching on isLoading again replaced every row with this spinner — which is
                the blanking the outer guard was added to stop, reintroduced two dozen lines
                later. Rows survive a refresh; the Refresh button carries the progress
                indication. [SC-313] */}
            {isLoading && availableRooms.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <LoadingState>
                    <CircularProgress />
                  </LoadingState>
                </TableCell>
              </TableRow>
            ) : availableRooms.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <MessageText>No rooms available</MessageText>
                </TableCell>
              </TableRow>
            ) : (
              availableRooms.map((room) => {
                // Debug log the complete room data
                console.log(`Room ${room.roomId} complete data:`, room)
                const hasPassword = checkRoomHasPassword(room)

                return (
                  <TableRowWrapper key={room.roomId}>
                    <TableCell>
                      <div className="name">{room.name}</div>
                      <div className="id">{room.roomId}</div>
                    </TableCell>
                    <TableCell>
                      <div className="description">{room.description || ''}</div>
                    </TableCell>
                    <TableCell>
                      <div className="creator">
                        {/* Try various properties to find creator name */}
                        {room.metadata?.playerName ||
                          room.metadata?.creatorName ||
                          room.creatorName ||
                          'Unknown'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="player-count">
                        <PeopleAltIcon />
                        {room.clients || room.metadata?.playerCount || 0}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="join-wrapper">
                        {hasPassword && (
                          <Tooltip title="Password Protected Room">
                            <LockIcon
                              className="lock-icon"
                              sx={{
                                color: 'warning.main',
                                animation: 'pulse 2s infinite',
                                '@keyframes pulse': {
                                  '0%': { opacity: 0.7 },
                                  '50%': { opacity: 1 },
                                  '100%': { opacity: 0.7 },
                                },
                              }}
                            />
                          </Tooltip>
                        )}
                        <ActionButton
                          variant="contained"
                          onClick={() => handleJoinClick(room.roomId, room.password || null)}
                          startIcon={hasPassword ? <LockIcon fontSize="small" /> : undefined}
                        >
                          {hasPassword ? 'Enter Password' : 'Join'}
                        </ActionButton>
                      </div>
                    </TableCell>
                  </TableRowWrapper>
                )
              })
            )}
          </TableBody>
        </Table>
      </CustomRoomTableContainer>
      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <ActionButton variant="outlined" onClick={onBack}>
          Back
        </ActionButton>
        <ActionButton variant="contained" onClick={() => refreshRoomData(true)}>
          <RefreshIcon sx={{ mr: 1 }} />
          Refresh
        </ActionButton>
      </Box>
      <PasswordDialog open={showPasswordDialog} onClose={resetPasswordDialog}>
        <DialogTitle>Room Password</DialogTitle>
        <form onSubmit={handlePasswordSubmit}>
          <DialogContent className="dialog-content">
            <MessageText>This is a private room. Please enter the password:</MessageText>
            <TextField
              autoFocus
              fullWidth
              error={passwordFieldEmpty}
              helperText={passwordFieldEmpty && 'Password is required'}
              value={password}
              label="Password"
              type="password"
              variant="outlined"
              InputLabelProps={{
                style: { color: 'rgba(255, 255, 255, 0.7)' },
              }}
              onInput={(e) => {
                setPassword((e.target as HTMLInputElement).value)
              }}
            />
            {showPasswordError && (
              <Alert severity="error" variant="filled">
                Incorrect Password!
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={resetPasswordDialog} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Cancel
            </Button>
            <ActionButton type="submit">Join Room</ActionButton>
          </DialogActions>
        </form>
      </PasswordDialog>
      <PermissionsDialog
        open={showPermissionsDialog}
        onClose={() => setShowPermissionsDialog(false)}
        onPermissionsGranted={handlePermissionsGranted}
        network={
          lobbyJoined && phaserGame.scene.keys.bootstrap
            ? (phaserGame.scene.keys.bootstrap as Bootstrap).network
            : undefined
        }
      />
    </>
  )
}
